import React, { memo, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';

import { libraryStates } from '../../utils/library-states';
import {
	classNames,
	formatCurrency,
	formatDate,
	formatLocalDateTime,
	formatLogValue,
	getDisplayValue,
	getFieldLabel,
	getLogBadgeStyle,
	getPaymentStateStyle,
	parseDateValue,
	statusStyleByValue,
} from './utils';

export const ColoredStatusSelect = memo(function ColoredStatusSelect({
	value,
	onChange,
	disabled,
}) {
	const normalizedValue = value ?? '';
	const styles = statusStyleByValue(normalizedValue);

	return (
		<div className='relative flex w-full min-w-0 items-center'>
			<select
				value={normalizedValue}
				disabled={disabled}
				onChange={(event) => onChange(event.target.value)}
				className={classNames(
					'h-7 w-full min-w-0 appearance-none rounded-full border border-transparent pl-6 pr-6 text-xs font-semibold shadow-sm outline-none',
					'ring-1 ring-inset transition',
					'focus:ring-4 focus:ring-sky-100 focus:outline-none',
					disabled && 'opacity-60 cursor-not-allowed',
					styles.wrap,
				)}
			>
				<option value='' disabled>
					—
				</option>
				{libraryStates.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>

			<span
				className={classNames(
					'pointer-events-none absolute left-2.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full',
					styles.dot,
				)}
			/>

			<svg
				className='pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-current opacity-70'
				width='14'
				height='14'
				viewBox='0 0 20 20'
				fill='currentColor'
			>
				<path
					fillRule='evenodd'
					d='M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z'
					clipRule='evenodd'
				/>
			</svg>
		</div>
	);
});

export const SalesRepSelect = memo(function SalesRepSelect({
	value,
	onChange,
	disabled,
	options = [],
}) {
	return (
		<div className='relative flex w-full min-w-0'>
			<select
				value={value ?? ''}
				disabled={disabled}
				onChange={(event) => onChange(event.target.value || null)}
				className={classNames(
					'h-9 w-full min-w-0 appearance-none rounded-lg border border-zinc-200 bg-white px-3 pr-8 text-xs font-medium shadow-sm outline-none',
					'text-zinc-700 hover:bg-zinc-50 focus:border-sky-300 focus:ring-4 focus:ring-sky-100',
					disabled && 'opacity-60 cursor-not-allowed',
				)}
			>
				<option value=''>—</option>
				{options.map((option) => (
					<option key={String(option.value)} value={String(option.value)}>
						{option.label}
					</option>
				))}
			</select>

			<svg
				className='pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400'
				width='14'
				height='14'
				viewBox='0 0 20 20'
				fill='currentColor'
			>
				<path
					fillRule='evenodd'
					d='M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z'
					clipRule='evenodd'
				/>
			</svg>
		</div>
	);
});

export const IconButton = memo(function IconButton({ title, onClick, children }) {
	return (
		<button
			type='button'
			title={title}
			onClick={onClick}
			className='inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 active:bg-zinc-200'
		>
			{children}
		</button>
	);
});

export const Select = memo(function Select({
	value,
	onChange,
	options,
	placeholder,
}) {
	return (
		<div className='relative'>
			<select
				value={value ?? ''}
				onChange={(event) => onChange(event.target.value || null)}
				className='h-9 w-full appearance-none rounded-lg border border-zinc-200 bg-white px-3 pr-8 text-xs text-zinc-800 shadow-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100'
			>
				<option value=''>{placeholder}</option>
				{options.map((option) => (
					<option key={String(option.value)} value={String(option.value)}>
						{option.label}
					</option>
				))}
			</select>
			<svg
				className='pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400'
				width='18'
				height='18'
				viewBox='0 0 20 20'
				fill='currentColor'
			>
				<path
					fillRule='evenodd'
					d='M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z'
					clipRule='evenodd'
				/>
			</svg>
		</div>
	);
});

export const EditableTextCell = memo(function EditableTextCell({
	value,
	onSave,
	disabled,
	placeholder = '—',
}) {
	const [localValue, setLocalValue] = useState(value ?? '');

	useEffect(() => {
		setLocalValue(value ?? '');
	}, [value]);

	return (
		<input
			value={localValue}
			disabled={disabled}
			onChange={(event) => setLocalValue(event.target.value)}
			onBlur={() => {
				const next = localValue.trim();
				const previous = (value ?? '').trim();
				if (next !== previous) onSave(next || null);
			}}
			onKeyDown={(event) => {
				if (event.key === 'Enter') event.currentTarget.blur();
				if (event.key === 'Escape') {
					setLocalValue(value ?? '');
					event.currentTarget.blur();
				}
			}}
			placeholder={placeholder}
			className={classNames(
				'h-9 w-full rounded-lg border border-zinc-200 bg-white px-3 text-xs text-zinc-700 outline-none',
				'focus:border-sky-300 focus:ring-4 focus:ring-sky-100',
				disabled && 'opacity-60 cursor-not-allowed',
			)}
		/>
	);
});

export const EditableDateCell = memo(function EditableDateCell({
	value,
	onSave,
	disabled,
}) {
	const [localValue, setLocalValue] = useState(parseDateValue(value));

	useEffect(() => {
		setLocalValue(parseDateValue(value));
	}, [value]);

	return (
		<div className='w-full min-w-0'>
			<DatePicker
				selected={localValue}
				onChange={(date) => {
					setLocalValue(date);
					onSave(date || null);
				}}
				disabled={disabled}
				timeIntervals={5}
				dateFormat='MMM dd, yyyy'
				placeholderText='Select date'
				className={classNames(
					'h-9 w-full rounded-lg border border-zinc-200 bg-white px-3 text-xs text-zinc-700 outline-none',
					'focus:border-sky-300 focus:ring-4 focus:ring-sky-100',
					disabled && 'opacity-60 cursor-not-allowed',
				)}
				popperPlacement='bottom-start'
			/>
		</div>
	);
});

export const MultiSelect = memo(function MultiSelect({
	options = [],
	value = [],
	onChange,
	placeholder = 'Select',
	label = 'item',
}) {
	const [open, setOpen] = useState(false);
	const containerRef = useRef(null);

	useEffect(() => {
		function handleClickOutside(event) {
			if (!containerRef.current) return;
			if (!containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		}

		function handleEscape(event) {
			if (event.key === 'Escape') {
				setOpen(false);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, []);

	const selectedOptions = options.filter((option) =>
		value.includes(String(option.value)),
	);

	const buttonLabel = selectedOptions.length
		? `${selectedOptions.length} ${label}${selectedOptions.length > 1 ? 's' : ''} selected`
		: placeholder;

	function toggleOption(optionValue) {
		const normalizedValue = String(optionValue);

		if (value.includes(normalizedValue)) {
			onChange(value.filter((item) => item !== normalizedValue));
		} else {
			onChange([...value, normalizedValue]);
		}
	}

	function clearAll(event) {
		event.stopPropagation();
		onChange([]);
	}

	return (
		<div ref={containerRef} className='relative min-w-[240px]'>
			<button
				type='button'
				onClick={() => setOpen((previous) => !previous)}
				className={classNames(
					'h-9 w-full rounded-lg bg-white px-3 pr-9 text-left text-xs text-zinc-800 shadow-sm outline-none',
					'focus:border-sky-300 focus:ring-4 focus:ring-sky-100',
					'hover:bg-zinc-50',
				)}
				style={{ border: '1px solid #e4e4e7' }}
			>
				<span className='block truncate'>{buttonLabel}</span>
			</button>

			<div className='pointer-events-none absolute inset-y-0 right-2 flex items-center gap-1'>
				{value.length > 0 && (
					<button
						type='button'
						onClick={clearAll}
						className='pointer-events-auto inline-flex h-5 w-5 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600'
						tabIndex={-1}
					>
						<svg width='14' height='14' viewBox='0 0 20 20' fill='currentColor'>
							<path
								fillRule='evenodd'
								d='M4.22 4.22a.75.75 0 011.06 0L10 8.94l4.72-4.72a.75.75 0 111.06 1.06L11.06 10l4.72 4.72a.75.75 0 11-1.06 1.06L10 11.06l-4.72 4.72a.75.75 0 01-1.06-1.06L8.94 10 4.22 5.28a.75.75 0 010-1.06z'
								clipRule='evenodd'
							/>
						</svg>
					</button>
				)}

				<svg
					className='text-zinc-400'
					width='18'
					height='18'
					viewBox='0 0 20 20'
					fill='currentColor'
				>
					<path
						fillRule='evenodd'
						d='M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z'
						clipRule='evenodd'
					/>
				</svg>
			</div>

			{open && (
				<div className='absolute z-30 mt-2 w-full overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-lg'>
					<div className='max-h-64 overflow-auto p-1'>
						{options.length === 0 ? (
							<div className='px-3 py-2 text-xs text-zinc-500'>No options</div>
						) : (
							options.map((option) => {
								const checked = value.includes(String(option.value));

								return (
									<button
										key={String(option.value)}
										type='button'
										onClick={() => toggleOption(option.value)}
										className={classNames(
											'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-xs',
											checked
												? 'bg-sky-50 text-sky-700'
												: 'text-zinc-700 hover:bg-zinc-50',
										)}
									>
										<div
											className={classNames(
												'flex h-4 w-4 items-center justify-center rounded border',
												checked
													? 'border-sky-500 bg-sky-500 text-white'
													: 'border-zinc-300 bg-white',
											)}
										>
											{checked && (
												<svg
													width='12'
													height='12'
													viewBox='0 0 20 20'
													fill='currentColor'
												>
													<path
														fillRule='evenodd'
														d='M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.414 0l-3.2-3.2a1 1 0 111.414-1.42l2.493 2.494 6.493-6.494a1 1 0 011.414 0z'
														clipRule='evenodd'
													/>
												</svg>
											)}
										</div>

										<span className='truncate'>{option.label}</span>
									</button>
								);
							})
						)}
					</div>
				</div>
			)}
		</div>
	);
});

export const LogsModal = memo(function LogsModal({
	open,
	institution,
	loading,
	error,
	data,
	count,
	page,
	pages,
	salesRepOptions,
	onClose,
	onPrev,
	onNext,
}) {
	if (!open) return null;

	return (
		<div className='fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4'>
			<div className='max-h-[88vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl'>
				<div className='border-b border-zinc-200 px-6 py-4'>
					<div className='flex items-start justify-between gap-4'>
						<div>
							<h3 className='text-lg font-semibold text-zinc-900'>
								Institution history log
							</h3>
							<p className='mt-1 text-xs text-zinc-500'>
								{institution?.name || 'Institution'} · {count} log
								{count === 1 ? '' : 's'}
							</p>
						</div>

						<button
							type='button'
							onClick={onClose}
							className='rounded-lg border border-zinc-200 px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50'
						>
							Close
						</button>
					</div>
				</div>

				<div className='hidden border-b border-zinc-200 bg-zinc-50/50 px-6 py-3 md:block'>
					<div className='grid grid-cols-[180px_minmax(0,1fr)_220px] gap-4 text-xs font-semibold uppercase tracking-wide text-zinc-500'>
						<div>Date</div>
						<div>Event</div>
						<div>User</div>
					</div>
				</div>

				<div className='max-h-[60vh] overflow-y-auto overflow-x-hidden'>
					{loading ? (
						<div className='px-6 py-12 text-center text-xs text-zinc-500'>
							Loading history...
						</div>
					) : error ? (
						<div className='px-6 py-12 text-center text-xs text-rose-600'>
							{error}
						</div>
					) : data.length === 0 ? (
						<div className='px-6 py-12 text-center text-xs text-zinc-500'>
							No history found for this institution.
						</div>
					) : (
						data.map((log) => (
							<div
								key={log.id}
								className='grid grid-cols-1 gap-3 border-b border-zinc-100 px-6 py-4 hover:bg-zinc-50 md:grid-cols-[180px_minmax(0,1fr)_220px]'
							>
								<div className='text-xs text-zinc-600'>
									<div className='mb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 md:hidden'>
										Date
									</div>
									{formatLocalDateTime(log.created_at)}
								</div>

								<div className='min-w-0 overflow-hidden'>
									<div className='flex flex-wrap items-center gap-2'>
										<span
											className={classNames(
												'inline-flex rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset',
												getLogBadgeStyle(log.FieldChanged),
											)}
										>
											{getFieldLabel(log.FieldChanged)}
										</span>

										<span className='text-xs text-zinc-800'>changed from</span>

										<span className='text-xs font-medium text-zinc-900'>
											{formatLogValue(
												log.FieldChanged,
												log.OldValue,
												salesRepOptions,
											)}
										</span>

										<span className='text-xs text-zinc-800'>to</span>

										<span className='text-xs font-medium text-zinc-900'>
											{formatLogValue(
												log.FieldChanged,
												log.NewValue,
												salesRepOptions,
											)}
										</span>
									</div>

									{log.Description && (
										<div className='mt-2 text-xs text-zinc-500'>
											{log.Description}
										</div>
									)}
								</div>

								<div className='text-xs text-zinc-600'>
									<div className='mb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 md:hidden'>
										User
									</div>
									<div className='font-medium text-zinc-800 break-all'>
										{log.email || '—'}
									</div>
								</div>
							</div>
						))
					)}
				</div>

				<div className='flex items-center justify-between border-t border-zinc-200 bg-white px-6 py-4'>
					<div className='text-xs text-zinc-500'>
						Page <span className='font-medium text-zinc-800'>{page}</span> of{' '}
						<span className='font-medium text-zinc-800'>{pages || 1}</span>
					</div>

					<div className='flex items-center gap-2'>
						<button
							type='button'
							onClick={onPrev}
							disabled={loading || page <= 1}
							className='rounded-lg border border-zinc-200 px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50'
						>
							Prev
						</button>

						<button
							type='button'
							onClick={onNext}
							disabled={loading || (pages && page >= pages)}
							className='rounded-lg border border-zinc-200 px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50'
						>
							Next
						</button>
					</div>
				</div>
			</div>
		</div>
	);
});

export const PaymentsModal = memo(function PaymentsModal({
	open,
	institution,
	loading,
	error,
	data,
	count,
	page,
	pages,
	onClose,
	onPrev,
	onNext,
}) {
	if (!open) return null;

	return (
		<div className='fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4'>
			<div className='max-h-[88vh] w-full max-w-6xl overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl'>
				<div className='border-b border-zinc-200 px-6 py-4'>
					<div className='flex items-start justify-between gap-4'>
						<div>
							<h3 className='text-lg font-semibold text-zinc-900'>
								Transactions
							</h3>
							<p className='mt-1 text-xs text-zinc-500'>
								{institution?.name || 'Institution'} · {count} transaction
								{count === 1 ? '' : 's'}
							</p>
						</div>

						<button
							type='button'
							onClick={onClose}
							className='rounded-lg border border-zinc-200 px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50'
						>
							Close
						</button>
					</div>
				</div>

				<div className='max-h-[62vh] overflow-auto'>
					{loading ? (
						<div className='px-6 py-12 text-center text-xs text-zinc-500'>
							Loading transactions...
						</div>
					) : error ? (
						<div className='px-6 py-12 text-center text-xs text-rose-600'>
							{error}
						</div>
					) : data.length === 0 ? (
						<div className='px-6 py-12 text-center text-xs text-zinc-500'>
							No transactions found for this institution.
						</div>
					) : (
						<table className='min-w-full border-collapse'>
							<thead className='sticky top-0 bg-white'>
								<tr className='border-b border-zinc-200'>
									<th className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500'>
										Date Paid
									</th>
									<th className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500'>
										Amount
									</th>
									<th className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500'>
										Frequency
									</th>
									<th className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500'>
										Next Payment
									</th>
									<th className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500'>
										Status
									</th>
									<th className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500'>
										User
									</th>
								</tr>
							</thead>

							<tbody className='bg-white'>
								{data.map((payment) => (
									<tr
										key={payment.id}
										className='border-b border-zinc-100 hover:bg-zinc-50'
									>
										<td className='px-6 py-4 text-xs text-zinc-700'>
											{formatDate(payment.date_payment)}
										</td>
										<td className='px-6 py-4 text-xs font-medium text-zinc-900'>
											{formatCurrency(payment.Payment_amount)}
										</td>
										<td className='px-6 py-4 text-xs text-zinc-700'>
											{payment.payment_type?.name || '—'}
										</td>
										<td className='px-6 py-4 text-xs text-zinc-700'>
											{formatDate(payment.Payment_end_date)}
										</td>
										<td className='px-6 py-4 text-xs font-medium'>
											<span
												className={classNames(
													getPaymentStateStyle(payment.payment_state?.name),
												)}
											>
												{payment.payment_state?.name || '—'}
											</span>
										</td>
										<td className='px-6 py-4 text-xs text-zinc-700 break-all'>
											{payment.user_email || '—'}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>

				<div className='flex items-center justify-between border-t border-zinc-200 bg-white px-6 py-4'>
					<div className='text-xs text-zinc-500'>
						Page <span className='font-medium text-zinc-800'>{page}</span> of{' '}
						<span className='font-medium text-zinc-800'>{pages || 1}</span>
					</div>

					<div className='flex items-center gap-2'>
						<button
							type='button'
							onClick={onPrev}
							disabled={loading || page <= 1}
							className='rounded-lg border border-zinc-200 px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50'
						>
							Prev
						</button>

						<button
							type='button'
							onClick={onNext}
							disabled={loading || pages <= 1 || page >= pages}
							className='rounded-lg border border-zinc-200 px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50'
						>
							Next
						</button>
					</div>
				</div>
			</div>
		</div>
	);
});

export const ChangeModal = memo(function ChangeModal({
	open,
	changeModal,
	salesRepOptions,
	savingCell,
	onClose,
	onConfirm,
	onDescriptionChange,
	onEmailChange,
}) {
	if (!open) return null;

	return (
		<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
			<div className='w-full max-w-lg rounded-2xl bg-white shadow-2xl'>
				<div className='border-b border-zinc-200 px-6 py-4'>
					<h3 className='text-lg font-semibold text-zinc-900'>Confirm change</h3>
					<p className='mt-1 text-xs text-zinc-500'>
						This change requires a log entry.
					</p>
				</div>

				<div className='px-6 py-5'>
					<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 hidden'>
						<div>
							<label className='mb-1 block text-xs font-semibold text-zinc-500 hidden'>
								INSTITUTION ID
							</label>
							<div className='rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-800'>
								{changeModal.rowId}
							</div>
						</div>

						<div>
							<label className='mb-1 block text-xs font-semibold text-zinc-500'>
								FIELD
							</label>
							<div className='rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-800'>
								{getFieldLabel(changeModal.field)}
							</div>
						</div>

						<div>
							<label className='mb-1 block text-xs font-semibold text-zinc-500'>
								OLD VALUE
							</label>
							<div className='rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-800'>
								{getDisplayValue(
									changeModal.field,
									changeModal.oldValue,
									salesRepOptions,
								)}
							</div>
						</div>

						<div>
							<label className='mb-1 block text-xs font-semibold text-zinc-500'>
								NEW VALUE
							</label>
							<div className='rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-800'>
								{getDisplayValue(
									changeModal.field,
									changeModal.newValue,
									salesRepOptions,
								)}
							</div>
						</div>
					</div>

					<div>
						<label className='block text-xs font-medium text-zinc-700'>
							Description
						</label>
						<textarea
							value={changeModal.description}
							onChange={onDescriptionChange}
							rows={4}
							placeholder='Why are you making this change?'
							className='w-full rounded-lg border border-zinc-200 px-3 py-2 text-xs outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100'
						/>
					</div>

					<div className='hidden'>
						<label className='mb-1 block text-xs font-medium text-zinc-700'>
							Email
						</label>
						<input
							type='email'
							value={changeModal.email}
							onChange={onEmailChange}
							placeholder='your@email.com'
							className='h-10 w-full rounded-lg border border-zinc-200 px-3 text-xs outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100'
						/>
					</div>
				</div>

				<div className='flex items-center justify-end gap-3 border-t border-zinc-200 px-6 py-4'>
					<button
						type='button'
						onClick={onClose}
						className='rounded-lg border border-zinc-200 px-4 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50'
					>
						Cancel
					</button>

					<button
						type='button'
						onClick={onConfirm}
						disabled={!!savingCell}
						className='rounded-lg bg-sky-600 px-4 py-2 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-50'
					>
						Save change
					</button>
				</div>
			</div>
		</div>
	);
});

export const Toast = memo(function Toast({ toast }) {
	if (!toast) return null;

	return (
		<div
			className={classNames(
				'fixed bottom-4 right-4 z-50 rounded-xl border px-4 py-2 text-xs shadow-lg',
				toast.type === 'success'
					? 'border-emerald-200 bg-emerald-50 text-emerald-800'
					: toast.type === 'error'
						? 'border-rose-200 bg-rose-50 text-rose-800'
						: 'border-zinc-200 bg-white text-zinc-800',
			)}
		>
			{toast.message}
		</div>
	);
});
